import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { DailyUsage, DeveloperInfo } from './developers';
import { fetchMyDeveloper } from './developers';
import { useAuth } from '../auth/AuthContext';

type DeveloperState =
  | { status: 'idle' } // 未ログイン
  | { status: 'loading' }
  | { status: 'unregistered' } // ログイン済みだが未申請
  | { status: 'registered'; developer: DeveloperInfo & { usage: DailyUsage[] } };

interface DeveloperContextValue {
  state: DeveloperState;
  reload: () => Promise<void>;
}

const DeveloperContext = createContext<DeveloperContextValue>({
  state: { status: 'idle' },
  reload: async () => {},
});

export function DeveloperProvider({ children }: { children: React.ReactNode }) {
  const { user, ready } = useAuth();
  const [state, setState] = useState<DeveloperState>({ status: 'loading' });

  const load = useCallback(async () => {
    if (!user) {
      setState({ status: 'idle' });
      return;
    }
    setState({ status: 'loading' });
    try {
      const dev = await fetchMyDeveloper();
      if (dev) setState({ status: 'registered', developer: dev });
      else setState({ status: 'unregistered' });
    } catch (e) {
      console.warn('開発者情報の取得に失敗:', e);
      // 失敗時は unregistered として扱い、登録導線が出るようにする。
      setState({ status: 'unregistered' });
    }
  }, [user]);

  useEffect(() => {
    if (!ready) return;
    void load();
  }, [ready, load]);

  return <DeveloperContext.Provider value={{ state, reload: load }}>{children}</DeveloperContext.Provider>;
}

export function useDeveloper(): DeveloperContextValue {
  return useContext(DeveloperContext);
}
